import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useScrollToElement } from 'typescript/hooks/use_scroll_to_element';
import { useAddOns } from 'typescript/pages/product/hooks/use_add_ons';
import { useUpdateItemQuantity } from 'typescript/pages/product/hooks/use_update_item_quantity';

// TODO: we'll eventually move these to be typescript files
import { STICKY_HEADER_OFFSET } from 'components/pages/product/helpers';
import { usePromotionEligibility } from 'components/pages/product/hooks/use_promotion_eligibility';
import {
  getKioskSoftwareAddOn,
  hasKioskSoftwarePricing,
  KioskSoftwareChoice,
} from '../kiosk_software';

// TODO: we'll eventually need to define a proper type for these or just swap to using RPC defined proto typings
type Product = Record<string, any>;
type Cart = Record<string, any>;
type Experiments = Record<string, any>;
type AddOns = Record<string, any>;
type CurrentUser = Record<string, any>;

interface Selection {
  kioskSoftware?: KioskSoftwareChoice | null;
  quantity: number;
  addOns: Record<string, string>;
}

interface ProductContextProps {
  children: ReactNode;
  product: Product;
  initialProduct?: Product;
  current_user: CurrentUser;
  cart: Cart;
  experiments: Experiments;
}

interface ProductContextType {
  product: Product;
  currentProduct: Product | null;
  setCurrentProduct: React.Dispatch<React.SetStateAction<Product | null>>;
  selection: Selection;
  setSelection: React.Dispatch<React.SetStateAction<Selection>>;
  addOns: AddOns;
  isGetAddOnsLoading: boolean;
  isGetAddOnsError: boolean;
  requiresKioskSoftwareSelection: boolean;
  isKioskSoftwareSelectionMissing: boolean;
  showKioskSoftwareError: boolean;
  kioskSoftwareRef: React.RefObject<HTMLElement>;
  cart: Cart | null;
  currentUser: CurrentUser;
  experiments: Experiments;
  onUpdateItemQuantity: ReturnType<
    typeof useUpdateItemQuantity
  >['onUpdateItemQuantity'];
  isUpdateItemQuantityLoading: boolean;
  scrollToRef: React.RefObject<HTMLDivElement>;
  scrollToElement: () => void;
  mainProductAdded: boolean;
  promoEligibility: Record<string, any>;

  chosenPickupLocationId: string | null;
  setChosenPickupLocationId: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  currentPickupOptions: Record<string, any>[];
  setCurrentPickupOptions: React.Dispatch<
    React.SetStateAction<Record<string, any>[]>
  >;
  bopisPostalCode: string;
  setBopisPostalCode: React.Dispatch<React.SetStateAction<string>>;
  bopisPickupLocation: Record<string, any> | null;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProductContext = () => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error(
      'useProductContext must be used within a ProductContextProvider',
    );
  }
  return context;
};

const ProductContextProvider = ({
  children,
  product,
  initialProduct = product,
  current_user,
  cart: initialCart,
  experiments,
}: ProductContextProps) => {
  // current product is the product that is currently being viewed
  // this could be the single product on the page or the selected alternative
  const [currentProduct, setCurrentProduct] = useState<Product | null>(
    initialProduct,
  );

  // selection is the quantity and add ons that are currently selected
  // ideally we eventually use react-hook-form to manage this but for now we'll just use a local state
  const [selection, setSelection] = useState<Selection>({
    quantity: 1,
    addOns: {},
    kioskSoftware: null,
  });

  // initial cart is the one that is passed in from our ruby backend
  // as items get added to cart, we'll update the cart state and use that instead of
  // tracking things like item count and subtotal using additional state
  // ideally in the future, we can also use react-query to manage the cart state
  const [cart, setCart] = useState<Cart | null>(initialCart);

  // tracks whether the main product has been added to cart (for showing upsell accordion)
  const [mainProductAdded, setMainProductAdded] = useState(false);

  // reset mainProductAdded when currentProduct changes (alternative selection)
  useEffect(() => {
    setMainProductAdded(false);
  }, [currentProduct?.slug]);

  // BOPIS related state
  const [chosenPickupLocationId, setChosenPickupLocationId] = useState(null);
  const [currentPickupOptions, setCurrentPickupOptions] = useState(
    currentProduct?.shipping?.bopis_stores,
  );
  const [bopisPostalCode, setBopisPostalCode] = useState(
    current_user.request_zip_code,
  );

  const bopisPickupLocation = useMemo(() => {
    return (
      currentPickupOptions?.find(
        (store) => store.location_id === chosenPickupLocationId,
      ) || currentPickupOptions?.[0]
    );
  }, [currentPickupOptions, chosenPickupLocationId]);

  // get addons, will auto update when currentProduct changes
  const { addOns, isGetAddOnsLoading, isGetAddOnsError } = useAddOns({
    product,
    currentProduct,
    cart,
    experiments,
  });

  const requiresKioskSoftwareSelection = Boolean(
    product.requires_kiosk_software_selection,
  );
  const [kioskSoftwareAttempted, setKioskSoftwareAttempted] = useState(false);
  const {
    elementRef: kioskSoftwareRef,
    scrollToElement: scrollToKioskSoftware,
  } = useScrollToElement(STICKY_HEADER_OFFSET);
  const kioskAddOn = getKioskSoftwareAddOn(addOns);
  const isKioskSoftwareSelectionMissing =
    requiresKioskSoftwareSelection &&
    (isGetAddOnsLoading ||
      !selection.kioskSoftware ||
      !hasKioskSoftwarePricing(kioskAddOn) ||
      selection.addOns[kioskAddOn?.category] !==
        kioskAddOn?.variant_id.toString());
  const showKioskSoftwareError =
    kioskSoftwareAttempted && isKioskSoftwareSelectionMissing;

  const handleKioskSoftwareSelectionMissing = (source: string) => {
    setKioskSoftwareAttempted(true);
    if (source === 'pdp-cta-subnav' && kioskSoftwareRef.current) {
      kioskSoftwareRef.current.focus({ preventScroll: true });
      scrollToKioskSoftware();
    }
  };

  // get promotion eligibility, will auto update when cart or currentProduct changes
  const { promoEligibility } = usePromotionEligibility(cart, currentProduct);

  // for the upsell and subtotal boxes, we'll scroll to the element when the main product is added to cart
  const { elementRef, scrollToElement } =
    useScrollToElement(STICKY_HEADER_OFFSET);

  const handleMainProductAdded = useCallback(() => {
    setMainProductAdded(true);
    scrollToElement();
  }, [scrollToElement]);

  const { onUpdateItemQuantity, isUpdateItemQuantityLoading } =
    useUpdateItemQuantity({
      currentProduct,
      cart,
      addOns,
      setCart,
      onMainProductAdded: handleMainProductAdded,
      kioskSoftwareSelection: requiresKioskSoftwareSelection
        ? selection.kioskSoftware
        : null,
      isKioskSoftwareSelectionMissing,
      onKioskSoftwareSelectionMissing: handleKioskSoftwareSelectionMissing,
    });

  const contextValue = useMemo(
    () => ({
      product,
      currentProduct,
      setCurrentProduct,
      selection,
      setSelection,
      addOns,
      isGetAddOnsLoading,
      isGetAddOnsError,
      requiresKioskSoftwareSelection,
      isKioskSoftwareSelectionMissing,
      showKioskSoftwareError,
      kioskSoftwareRef,
      cart,
      currentUser: current_user,
      experiments,
      onUpdateItemQuantity,
      isUpdateItemQuantityLoading,
      scrollToRef: elementRef,
      scrollToElement,
      mainProductAdded,
      promoEligibility,
      chosenPickupLocationId,
      setChosenPickupLocationId,
      currentPickupOptions,
      setCurrentPickupOptions,
      bopisPostalCode,
      setBopisPostalCode,
      bopisPickupLocation,
    }),
    [
      product,
      currentProduct,
      selection,
      addOns,
      isGetAddOnsLoading,
      isGetAddOnsError,
      requiresKioskSoftwareSelection,
      isKioskSoftwareSelectionMissing,
      showKioskSoftwareError,
      kioskSoftwareRef,
      cart,
      experiments,
      onUpdateItemQuantity,
      isUpdateItemQuantityLoading,
      scrollToElement,
      mainProductAdded,
      promoEligibility,
      chosenPickupLocationId,
      currentPickupOptions,
      bopisPostalCode,
      bopisPickupLocation,
      current_user,
    ],
  );

  return (
    <ProductContext.Provider value={contextValue}>
      {children}
    </ProductContext.Provider>
  );
};

export { ProductContextProvider };
